import { Link } from "react-router-dom";
import { Phone, Send, ShieldCheck, MapPin, AlertTriangle, RefreshCw } from "lucide-react";
import HVACShell from "../components/hvac/HVACShell";
import { Band, SectionHead, PricingGrid, ReviewCards, FaqGroups, MoreLink } from "../components/hvac/HVACBlocks";
import { leesServiceSchemas } from "../seo/schema";
import { hvacPath } from "../seo/hosts";
import { LEES_CONTACT, LEES_PRICING, LEES_REVIEWS, LEES_FAQ_GROUPS, LEES_SERVICE_AREAS } from "../data/leesContent";
import { HVAC_SERVICES, HVAC_PHOTOS } from "../data/leesServices";

const TEASER_PRICES = ["Diagnostic / Service Visit", "AC Precision Tune-Up & Cleaning Check", "AC + Furnace Same-Visit Bundle"];
const TEASER_FAQS = ["Do you offer free diagnostic visits?", "Why is my AC running but not cooling?", "Can an older air conditioner still be repaired?", "Do you offer emergency HVAC service?"];
const teaserFaqGroup = [{ key: "top", title: "Top Questions", items: LEES_FAQ_GROUPS.flatMap((g) => g.items).filter((i) => TEASER_FAQS.includes(i.q)) }];

function Hero() {
  return (
    <section className="hvac-grain border-b-2 border-[#8B1E1E]">
      <div className="max-w-6xl mx-auto px-5 py-16 md:py-24 grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
        <div className="lg:col-span-7">
          <p className="inline-block hvac-stencil text-[#EF4444] text-xs md:text-sm font-bold uppercase tracking-[0.25em] border border-[#8B1E1E] px-3 py-1.5 mb-5">Vietnam-Veteran Legacy · Family-Owned · NW Indiana</p>
          <h1 className="hvac-stencil hvac-stamp text-4xl sm:text-5xl lg:text-6xl font-bold leading-[1.05] uppercase">
            Honest HVAC Work for <span className="text-[#D32F2F]">Northwest Indiana</span> Homes and Businesses.
          </h1>
          <p className="mt-6 text-base md:text-lg text-slate-300 leading-relaxed max-w-2xl">
            Family-owned HVAC company serving Lake Station, Hobart, Gary, Portage, Merrillville and surrounding
            Northwest Indiana. Emergency repairs, AC, furnaces, maintenance, refrigeration, mini-splits, and light commercial.
          </p>
          <div className="mt-8 flex flex-wrap gap-3">
            <a href="#hvac-quote" className="inline-flex items-center gap-2 bg-[#8B1E1E] hover:bg-[#A6271F] text-white font-bold px-6 py-3 rounded-sm uppercase tracking-wide text-sm transition-colors" data-testid="hvac-hero-cta"><Send size={16} /> Request a Free Quote</a>
            <a href={LEES_CONTACT.phoneHref} className="inline-flex items-center gap-2 hover:bg-white/10 text-white font-bold px-6 py-3 rounded-sm border-2 border-white uppercase tracking-wide text-sm transition-colors" data-testid="hvac-hero-call"><Phone size={16} /> Call {LEES_CONTACT.phone}</a>
          </div>
          <div className="mt-7 flex flex-wrap gap-4 text-xs text-slate-400 uppercase tracking-wider">
            {["Honest pricing", "Family-owned", "Show up. Tell the truth."].map((t) => (
              <span key={t} className="inline-flex items-center gap-2"><ShieldCheck size={14} className="text-[#EF4444]" aria-hidden="true" /> {t}</span>
            ))}
          </div>
        </div>
        <div className="lg:col-span-5 relative">
          <div className="absolute inset-0 border-2 border-[#8B1E1E] translate-x-3 translate-y-3" />
          <img src={HVAC_PHOTOS.roof} srcSet={`${HVAC_PHOTOS.roofSmall} 640w, ${HVAC_PHOTOS.roof} 1080w`} sizes="(min-width: 1024px) 460px, 100vw" width="1080" height="810" fetchPriority="high" decoding="async" alt="HVAC technician servicing a rooftop unit in Northwest Indiana" className="relative w-full h-[360px] md:h-[440px] object-cover grayscale-[20%] contrast-[1.05]" />
        </div>
      </div>
    </section>
  );
}

function ServicesTeaser() {
  return (
    <Band id="services" testid="hvac-services" wide>
      <SectionHead eyebrow="What We Do" title="Services" intro="Tap any service for details, or request a visit below." />
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {HVAC_SERVICES.map(({ key, label, blurb, icon: Icon, page }) => (
          <Link key={key} to={hvacPath(page)} className="hvac-card p-6 block group" data-testid={`hvac-service-${key}`}>
            <div className="h-10 w-10 inline-flex items-center justify-center bg-[#8B1E1E] text-white mb-4"><Icon size={18} /></div>
            <h3 className="hvac-stencil text-lg font-bold uppercase tracking-wide">{label}</h3>
            <p className="text-sm text-slate-400 mt-2 leading-relaxed">{blurb}</p>
            <span className="hvac-stencil text-xs uppercase tracking-[0.2em] text-[#FF6B4A] mt-4 inline-block group-hover:translate-x-1 transition-transform">Learn more →</span>
          </Link>
        ))}
      </div>
    </Band>
  );
}

function SplitTeasers() {
  const cards = [
    { icon: RefreshCw, title: "Told you need a new system?", text: "Older equipment isn't automatically unrepairable. Get an honest second opinion before you replace.", to: "/second-opinion", cta: "Get a Second Opinion", testid: "hvac-teaser-second-opinion" },
    { icon: AlertTriangle, title: "No heat? No cooling?", text: "Submit a request, mark it as an emergency, then call or text so we know it's waiting.", to: "/emergency-hvac", cta: "Emergency HVAC", testid: "hvac-teaser-emergency" },
  ];
  return (
    <Band light testid="hvac-split-teasers">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
        {cards.map(({ icon: Icon, ...c }) => (
          <article key={c.to} className="border border-slate-200 rounded-sm p-7" data-testid={c.testid}>
            <Icon size={22} className="text-[#8B1E1E]" />
            <h2 className="hvac-stencil text-2xl font-bold uppercase mt-3">{c.title}</h2>
            <p className="text-slate-600 mt-2 leading-relaxed">{c.text}</p>
            <Link to={hvacPath(c.to)} className="hvac-stencil inline-block mt-5 text-sm font-bold uppercase tracking-wider text-[#8B1E1E] hover:text-black" data-testid={`${c.testid}-link`}>{c.cta} →</Link>
          </article>
        ))}
      </div>
    </Band>
  );
}

function LegacyTeaser() {
  return (
    <Band testid="hvac-legacy-teaser">
      <div className="max-w-3xl mx-auto text-center">
        <p className="hvac-stencil text-[#EF4444] text-xs font-bold uppercase tracking-[0.3em] mb-3">The Legacy</p>
        <h2 className="hvac-stencil text-3xl md:text-4xl font-bold uppercase leading-tight">Three Generations of Faith, Family &amp; Craftsmanship</h2>
        <p className="mt-5 text-base md:text-lg leading-relaxed text-slate-300">Lee&apos;s Heating &amp; Cooling is built upon generations of faith, honesty, craftsmanship, and serving others.</p>
        <Link to="/legacy" className="inline-flex items-center gap-2 mt-8 bg-[#8B1E1E] hover:bg-[#A6271F] text-white font-bold px-6 py-3 rounded-sm uppercase tracking-wide text-sm transition-colors" data-testid="hvac-legacy-cta">Read Our Legacy →</Link>
      </div>
    </Band>
  );
}

function AreaTeaser() {
  return (
    <Band testid="hvac-area-teaser">
      <SectionHead eyebrow="Service Area" title="Serving Northwest Indiana" />
      <div className="flex flex-wrap justify-center gap-2 max-w-3xl mx-auto">
        {LEES_SERVICE_AREAS.core.map((c) => (
          <span key={c} className="inline-flex items-center gap-2 px-4 py-2 border border-[#8B1E1E] text-sm text-slate-200 hvac-stencil uppercase tracking-wide" data-testid={`hvac-area-${c.toLowerCase().replace(/\s/g, "-")}`}><MapPin size={12} className="text-[#8B1E1E]" /> {c}</span>
        ))}
      </div>
      <p className="text-center text-sm text-slate-400 mt-5">Plus {LEES_SERVICE_AREAS.surrounding.length} surrounding communities, from Hammond to La Porte.</p>
      <MoreLink to="/services#service-area" testid="hvac-area-more">See full service area</MoreLink>
    </Band>
  );
}

export default function HVACLanding() {
  return (
    <HVACShell
      path="/"
      testid="hvac-landing"
      title="Lee's Heating & Cooling | HVAC Repair, AC, Furnace & Maintenance in Northwest Indiana"
      description="Family-owned HVAC in Northwest Indiana. Emergency HVAC, AC repair, furnace repair, maintenance & tune-ups, mini-splits, refrigeration, and light commercial. Serving Lake Station, Hobart, Gary, Portage, Merrillville & surrounding NWI."
      schemas={leesServiceSchemas()}
    >
      <Hero />
      <div className="hvac-divider" />
      <ServicesTeaser />
      <Band light id="pricing-teaser" testid="hvac-pricing-teaser">
        <SectionHead light eyebrow="Pricing" title="Honest, Transparent Pricing" intro="Starting points shown — we disclose pricing before additional repair work proceeds." />
        <PricingGrid items={LEES_PRICING.filter((p) => TEASER_PRICES.includes(p.label))} />
        <MoreLink to="/pricing" testid="hvac-pricing-more">See all pricing</MoreLink>
      </Band>
      <Band testid="hvac-reviews-teaser">
        <SectionHead eyebrow="Reviews" title="Real customers. Real reviews." />
        <ReviewCards reviews={LEES_REVIEWS.slice(0, 2)} />
        <MoreLink to="/reviews" testid="hvac-reviews-more">Read all reviews</MoreLink>
      </Band>
      <SplitTeasers />
      <LegacyTeaser />
      <Band light testid="hvac-faq-teaser">
        <SectionHead light eyebrow="HVAC Answer Center" title="Quick Answers" />
        <FaqGroups groups={teaserFaqGroup} testid="hvac-faq-teaser-list" />
        <MoreLink to="/faq" testid="hvac-faq-more">View all questions</MoreLink>
      </Band>
      <AreaTeaser />
    </HVACShell>
  );
}
